import {
  AlpService,
  AssessmentService,
  IActiveSchedule,
  IAlp,
  IAlpWithLessons,
  IUser
} from "@imaginelearning/mps-api-service";
import { IAssessment } from "../types";
import disableFutureAssessments from "./disableFutureAssessments";

type assessmentType = "diagnostic" | "benchmark" | "formative" | "screener";

const assessmentTypeOrder: Record<assessmentType, number> = {
  diagnostic: 1,
  screener: 2, // for EarlyBird
  benchmark: 3,
  formative: 4
};

const subjectOrder: Record<string, number> = {
  reading: 1,
  ela: 1,
  math: 2
};

export const fetchFormatives = (user: IUser) =>
  AlpService.getAlps(user.id, user.schoolYear, "Assessment")
    .then((response) => {
      const alpResponse = response.response as IAlp[];
      if (alpResponse) {
        const items: IAssessment[] = [];
        alpResponse
          .filter((alp) => !alp.isCompleted)
          .forEach((alp: IAlp) => {
            if (alp.assignmentInfo.assessments?.length > 0) {
              const card = mapFormativeAssignmentToIAssessment(alp);
              items.push(card);
            }
          });
        return items;
      }
      return [];
    })
    .catch((error) => {
      console.error("Error:", error);
      return [];
    }) as Promise<IAssessment[]>;

export const fetchDiagnosticsAndBenchmarks = (user: IUser) =>
  AssessmentService.getStudentSchedule(user.id, user.schoolYear)
    .then((response) => {
      if (response.response && typeof response.response !== "string") {
        const data = response.response as IActiveSchedule[];

        if (data && data.length > 0) {
          const recordsToDisplay = data.filter((record) => record.testStatus.toLowerCase() !== "completed");
          const newAssessments = mapAssessmentSchedulesToIAssessment(recordsToDisplay);
          // Merge new assessments into the current state
          return newAssessments;
        }
      }
      return [];
    })
    .catch((error) => {
      console.error("Error fetching scheduler assessments", error);
    }) as Promise<IAssessment[]>;

export const mapFormativeAssignmentToIAssessment = (alp: IAlpWithLessons): IAssessment => {
  const progress =
    alp.assignmentInfo.assessments.length === 0
      ? 0
      : Math.round(
          (alp.assignmentInfo.assessments.filter((assessment) => assessment.status === "Completed").length /
            alp.assignmentInfo.assessments.length) *
            100
        );
  const card: IAssessment = {
    id: alp.assignmentInfo.assignmentID,
    title: alp.assignmentInfo.title,
    state: alp.state,
    endDate: alp.assignmentInfo.dueDate,
    dueDate: alp.assignmentInfo.dueDate,
    subject: alp.subject.toLowerCase() === "reading" ? "ELA" : alp.subject,
    assessmentType: "formative",
    icon: alp.assignmentInfo.icon,
    progress,
    assessments: alp.assignmentInfo.assessments
      .toSorted((a, b) => a.order - b.order)
      .map((assessment) => ({
        ...assessment,
        parentAssignmentId: alp.assignmentInfo.assignmentID,
        subject: alp.subject
      }))
  };
  return card;
};

export const mapAssessmentSchedulesToIAssessment = (data: IActiveSchedule[]): IAssessment[] =>
  data.map((schedule) => {
    const progress = statusToProgress(schedule.testStatus);

    const assessment: IAssessment = {
      id: schedule.assessmentName,
      title: schedule.assessmentName,
      startDate: schedule.startDate,
      endDate: schedule.endDate,
      dueDate:
        schedule.expirationDate && new Date(schedule.expirationDate) < new Date(schedule.endDate)
          ? schedule.expirationDate
          : schedule.endDate,
      subject: schedule.subject,
      assessmentType: schedule.assessmentType.toLowerCase(), // normalize to lowercase
      icon: "default-icon", // Placeholder, replace with an actual icon mapping if needed
      progress,
      testNumber: schedule.testNumber,
      isRetake: !!schedule.resetDate
    };

    return assessment;
  });

function statusToProgress(status: string): number {
  switch (status) {
    case "Completed":
      return 100;
    case "InProgress":
      return 50;
    default:
      return 0;
  }
}

export const filterAssessments = (rawAssessments: IAssessment[], subject: string) => {
  const filteredassessments = rawAssessments.filter(
    (assessment) => subject === "All" || assessment.subject.toLowerCase() === subject.toLowerCase()
  );

  const modifiedAssessments = disableFutureAssessments(filteredassessments, ["diagnostic", "screener"]);

  const sortedAssessments = modifiedAssessments.toSorted((a: IAssessment, b: IAssessment) => {
    const assessmentTypeComparison =
      (assessmentTypeOrder[a.assessmentType as assessmentType] || Infinity) -
      (assessmentTypeOrder[b.assessmentType as assessmentType] || Infinity);

    if (assessmentTypeComparison !== 0) {
      return assessmentTypeComparison;
    }

    const subjectA = a.subject.toLowerCase();
    const subjectB = b.subject.toLowerCase();

    return (subjectOrder[subjectA] || Infinity) - (subjectOrder[subjectB] || Infinity);
  });

  return sortedAssessments;
};
